import React, { useEffect, useMemo, useState } from 'react';
import inventoryApi from '../../../../services/inventoryApi';
import toast from '../../../../utils/toast';

const ACCENT = '#7d6633';

const UpdateStockModal = ({ isOpen, onClose, onSuccess, item }) => {
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [locationId, setLocationId] = useState('');

  const currentStock = useMemo(() => {
    if (!item) return 0;
    return Number.isFinite(item.currentStock) ? item.currentStock : (item.totalStock || 0);
  }, [item]);

  useEffect(() => {
    if (!isOpen) return;
    setQuantity('');

    const loadLocation = async () => {
      if (item?.locationId) {
        setLocationId(String(item.locationId));
        return;
      }
      try {
        const locationsRes = await inventoryApi.locations.getLocations();
        const firstLocation = locationsRes.locations?.[0];
        setLocationId(firstLocation?.id ? String(firstLocation.id) : '');
      } catch (error) {
        console.error('Error loading locations:', error);
        toast.error('Failed to load locations');
      }
    };

    loadLocation();
  }, [isOpen, item]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    if (!item?.id) {
      toast.error('Missing product details');
      return;
    }
    if (!locationId) {
      toast.error('No location available');
      return;
    }

    setLoading(true);
    try {
      await inventoryApi.movements.createReceive({
        locationId: parseInt(locationId, 10),
        lines: [{ itemId: item.id, qty }]
      });
      toast.success('Stock updated');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error(error?.response?.data?.error || 'Failed to update stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div
        className="w-full max-w-md rounded-2xl border border-white/20 dark:border-white/10 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl shadow-2xl"
        style={{ '--accent': ACCENT }}
      >
        <div className="p-5 border-b border-white/10 dark:border-white/10">
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Update Stock</h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            Use this when you receive new products from your supplier.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Product</label>
            <div className="mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {item?.name || 'Selected product'}
            </div>
          </div>

          <div className="rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-800/60 p-4">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">Current stock</p>
            <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
              {currentStock}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Quantity received
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity"
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-800/60 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/40 transition-all"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-800/60 text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-xl text-white font-medium transition-all shadow-lg disabled:opacity-50"
              style={{ background: ACCENT }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateStockModal;
